import { setSparkflowGlobalContext } from '@thoughtspot/blink-context';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { jsonConstants } from '@thoughtspot/json-constants';
import { BannerAlertType } from '@thoughtspot/radiant-react/widgets/alert';
import React, { useEffect, useState } from 'react';
import { useBlinkContextForSparkflow } from "../../contexts/sparkflow-blink-context";
import { getSparkflowConfig } from "../../contexts/sparkflow-context";
import { useSaveSpotbuildGeneratedIds, useUpdateFreeTrialOnboardingMutation, } from "../../services/onboarding.service";
import { agentCardClicked, agentSelectionConfirmed, agentSelectionPageErrorShown, agentSelectionPageLoaded, PageContext, } from "../../utils/metrics/metrics-utils";
import { getOrderedAgentOptions, SaveAgentIdMapping, } from "../../utils/sparkflow-constants";
import { AgentId } from "../../utils/types";
import { AgentSelection } from './agent-selection';
export const AgentSelectionContainer = () => {
    const { navService, metricsService, sessionService, } = useBlinkContextForSparkflow();
    const [bannerAlertMessage, setBannerAlertMessage] = useState({
        isVisible: false,
        errorMessage: '',
    });
    const { t } = useTranslation();
    const { buildInfo, externalConfig } = getSparkflowConfig();
    const { updateSparkflowGlobalContext, requestChecklistRefresh, } = setSparkflowGlobalContext();
    const isSparkflowUsecaseBasedOnboardingEnabled = sessionService?.isSparkflowUsecaseBasedOnboardingEnabled?.();
    const orderedOptions = getOrderedAgentOptions(isSparkflowUsecaseBasedOnboardingEnabled);
    const { saveSpotbuildGeneratedIds, error: saveSpotbuildError, } = useSaveSpotbuildGeneratedIds();
    const [selectedAgent, setSelectedAgent] = useState(orderedOptions[0].id);
    const featuredOption = orderedOptions[0];
    const otherOptions = orderedOptions.slice(1);
    const preSelectedAgent = orderedOptions[0].id;
    const { onboardingSelections } = getSparkflowConfig();
    useEffect(() => {
        agentSelectionPageLoaded(metricsService, {
            pageContext: PageContext.AGENT_SELECTION_PAGE,
            selectedFunction: onboardingSelections?.selectedFunction || '',
            selectedIndustry: onboardingSelections?.selectedIndustry || '',
            preSelectedAgent,
        });
    }, []);
    useEffect(() => {
        if (saveSpotbuildError) {
            setBannerAlertMessage({
                isVisible: true,
                errorMessage: saveSpotbuildError.message,
            });
        }
    }, [saveSpotbuildError]);
    const userName = sessionService?.getUserDisplayName();
    const defaultPinboardName = t('sparkflow.default.liveboard', {
        name: userName,
    });
    const useCreatePinboardByNameMutation = externalConfig?.services?.useCreatePinboardByNameMutation;
    const { _createPinboard } = useCreatePinboardByNameMutation();
    const { Session__updateFreeTrialOnboardingConfig, } = useUpdateFreeTrialOnboardingMutation();
    const handleGetStarted = async () => {
        try {
            const onboardingLocation = SaveAgentIdMapping[selectedAgent];
            if (!onboardingLocation) {
                throw new Error(`Unrecognised agent id: ${selectedAgent}`);
            }
            const promises = [
                Session__updateFreeTrialOnboardingConfig(false, onboardingLocation, true),
            ];
            if (sessionService.isOnboardingMTServicesEnabled()) {
                promises.push(saveSpotbuildGeneratedIds([{ agent_id: onboardingLocation }]));
            }
            await Promise.all(promises);
            sessionService.setEdgeOnboardingCompleted(true);
            sessionService.setOnboardingLocationSelected(onboardingLocation);
            if (sessionService.isSparkflowV2Enabled()) {
                requestChecklistRefresh({ checklistRefreshRequested: true });
            }
            if (selectedAgent === AgentId.ASK_QUESTIONS) {
                navService?.goToAskSageChat([
                    { name: 'worksheetId', value: buildInfo?.modelId },
                    { name: jsonConstants.IS_FROM_SPARKFLOW, value: true },
                ]);
            }
            else if (selectedAgent === AgentId.BUILD_DASHBOARD) {
                const response = await _createPinboard({
                    variables: { name: defaultPinboardName },
                });
                const newLiveboardId = response?.data?.Pinboard__createByName?.id;
                if (newLiveboardId) {
                    updateSparkflowGlobalContext({
                        isLoadingIndicatorVisible: true,
                        hideSparkflowBanner: true,
                    });
                    navService?.goToSparkflowPinboard(newLiveboardId, [
                        { name: 'mode', value: 'edit' },
                    ]);
                }
            }
            else if (selectedAgent === AgentId.EMBED_INSIGHTS) {
                navService?.goToDevelopTab([
                    { name: jsonConstants.IS_FROM_SPARKFLOW, value: true },
                ]);
            }
            agentSelectionConfirmed(metricsService, {
                pageContext: PageContext.AGENT_SELECTION_PAGE,
                selectedFunction: onboardingSelections?.selectedFunction || '',
                selectedIndustry: onboardingSelections?.selectedIndustry || '',
                preSelectedAgent,
                agentCardSelected: selectedAgent,
            });
        }
        catch (error) {
            setBannerAlertMessage({
                isVisible: true,
                errorMessage: error?.message,
            });
            agentSelectionPageErrorShown(metricsService, {
                pageContext: PageContext.AGENT_SELECTION_PAGE,
                selectedFunction: onboardingSelections?.selectedFunction || '',
                selectedIndustry: onboardingSelections?.selectedIndustry || '',
                preSelectedAgent,
                agentCardSelected: selectedAgent,
                errorType: error?.message || 'Navigation error',
            });
        }
    };
    const handleSelectAgent = (agentId) => {
        setSelectedAgent(agentId);
        agentCardClicked(metricsService, {
            pageContext: PageContext.AGENT_SELECTION_PAGE,
            selectedFunction: onboardingSelections?.selectedFunction || '',
            selectedIndustry: onboardingSelections?.selectedIndustry || '',
            preSelectedAgent,
            agentCardSelected: agentId,
        });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(GlobalAlert.Banner, { type: BannerAlertType.ERROR, "data-testid": "agent-selection-banner-alert", message: bannerAlertMessage.errorMessage, isVisible: bannerAlertMessage.isVisible, onClose: () => setBannerAlertMessage({
                isVisible: false,
                errorMessage: '',
            }) }),
        React.createElement(AgentSelection, { featuredOption: featuredOption, otherOptions: otherOptions, selectedAgent: selectedAgent, onSelectAgent: handleSelectAgent, onGetStarted: handleGetStarted })));
};
//# sourceMappingURL=agent-selection.container.js.map